import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { adminService, AdminPlan, ComparisonFeature, Coupon } from '../services/adminService';
import api from '../services/api';

interface TopUpPack {
  id: string;
  label: string;
  credits: number;
  price: number;
  currency: string;
  isActive: boolean;
  popular: boolean;
  sortOrder: number;
}

const emptyPack = (sortOrder = 10): TopUpPack => ({
  id: '', label: '', credits: 10, price: 99, currency: 'INR', isActive: true, popular: false, sortOrder,
});

const normalizePack = (pack: any, idx = 0): TopUpPack => ({
  id: String(pack.id || ''),
  label: String(pack.label || ''),
  credits: Number(pack.credits || 0),
  price: Number(pack.price || 0),
  currency: String(pack.currency || 'INR'),
  isActive: pack.isActive !== false,
  popular: Boolean(pack.popular),
  sortOrder: Number(pack.sortOrder ?? idx + 1),
});

const OperationsPlans: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Plans & TopUps</Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Subscription Plans" />
          <Tab label="Top-up Packs" />
          <Tab label="Coupons" />
          <Tab label="Feature Comparison" />
        </Tabs>
      </Box>

      {tabIndex === 0 && <PlansTab />}
      {tabIndex === 1 && <TopUpsTab />}
      {tabIndex === 2 && <CouponsTab />}
      {tabIndex === 3 && <FeatureComparisonTab />}
    </Box>
  );
};

// ── Plans Tab ────────────────────────────────────────────────────────────────

const PlansTab: React.FC = () => {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [freePlanSaving, setFreePlanSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeletePlan, setConfirmDeletePlan] = useState<AdminPlan | null>(null);
  const [newPlan, setNewPlan] = useState({
    name: '', description: '', price: 1, monthlyLeadLimit: 10, topUpAllowed: true, earlyAccessMinutes: 0, features: [] as string[],
  });
  const [newPlanFeatureInput, setNewPlanFeatureInput] = useState('');
  const [freePlanFeatureInput, setFreePlanFeatureInput] = useState('');
  const [featureInputs, setFeatureInputs] = useState<Record<string, string>>({});

  const getFeatureInput = (id: string) => featureInputs[id] ?? '';
  const setFeatureInput = (id: string, val: string) => setFeatureInputs((prev) => ({ ...prev, [id]: val }));

  useEffect(() => {
    adminService.getPlans().then(setPlans).catch(() => toast.error('Failed to load plans')).finally(() => setLoading(false));
  }, []);

  const updateField = (id: string, field: keyof AdminPlan, value: any) =>
    setPlans((prev) => prev.map((p) => p._id === id ? { ...p, [field]: value } : p));

  const freePlan = plans.find((plan) => plan.slug === 'free') || null;
  const paidPlans = plans.filter((plan) => plan.slug !== 'free');

  const saveFreePlan = async () => {
    if (!freePlan) return;
    try {
      setFreePlanSaving(true);
      const saved = await adminService.updateFreePlanSettings({
        name: freePlan.name,
        description: freePlan.description,
        features: freePlan.features,
      });
      setPlans((prev) => prev.map((plan) => plan.slug === 'free' ? saved : plan));
      toast.success('Free plan settings updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update Free plan settings');
    } finally {
      setFreePlanSaving(false);
    }
  };

  const save = async (plan: AdminPlan) => {
    try {
      setSavingId(plan._id);
      const saved = await adminService.updatePlan(plan._id, plan);
      setPlans((prev) => prev.map((p) => p._id === saved._id ? saved : p));
      toast.success('Plan updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update plan');
    } finally {
      setSavingId(null);
    }
  };

  const deletePlan = async (plan: AdminPlan) => {
    try {
      setDeletingId(plan._id);
      await adminService.deletePlan(plan._id);
      setPlans((prev) => prev.filter((p) => p._id !== plan._id));
      setConfirmDeletePlan(null);
      toast.success('Plan deleted');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete plan';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const create = async () => {
    if (!newPlan.name.trim()) { toast.error('Plan name is required'); return; }
    if (Number(newPlan.price) < 1) { toast.error('Paid plan price must be at least 1'); return; }
    try {
      const created = await adminService.createPlan({
        name: newPlan.name,
        description: newPlan.description,
        price: Number(newPlan.price),
        monthlyLeadLimit: Number(newPlan.monthlyLeadLimit),
        topUpAllowed: newPlan.topUpAllowed,
        earlyAccessMinutes: Number(newPlan.earlyAccessMinutes),
        features: newPlan.features,
        teamSeats: 1, canExport: false, prioritySupport: false,
        billingInterval: 'monthly', currency: 'INR', isActive: true,
      });
      setPlans((prev) => [...prev, created].sort((a, b) => a.price - b.price));
      setNewPlan({ name: '', description: '', price: 1, monthlyLeadLimit: 10, topUpAllowed: true, earlyAccessMinutes: 0, features: [] });
      setNewPlanFeatureInput('');
      toast.success('Plan created');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create plan');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        The built-in Free plan is always active and uses wallet credits. Other subscription plans are created and managed below.
      </Alert>

      {freePlan && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.03)' }}>
          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="h6" fontWeight={700}>Free Plan Settings</Typography>
                <Typography variant="body2" color="text.secondary">
                  Built-in fallback plan. Price, monthly quota, status, slug, and entitlements are locked.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip label="Always active" color="success" size="small" />
                <Chip label="Wallet credits" color="primary" variant="outlined" size="small" />
              </Stack>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Display name"
                  value={freePlan.name}
                  onChange={(e) => updateField(freePlan._id, 'name', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth label="Price" value={0} disabled />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth label="Monthly unlocks" value={0} disabled />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Slug" value="free" disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description (shown under plan name)"
                  value={freePlan.description || ''}
                  onChange={(e) => updateField(freePlan._id, 'description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Feature bullet points
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                  {(freePlan.features || []).map((feature, index) => (
                    <Chip
                      key={`${feature}-${index}`}
                      label={feature}
                      size="small"
                      onDelete={() => updateField(
                        freePlan._id,
                        'features',
                        freePlan.features.filter((_, featureIndex) => featureIndex !== index)
                      )}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="e.g. Free welcome credits at signup"
                    value={freePlanFeatureInput}
                    onChange={(e) => setFreePlanFeatureInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && freePlanFeatureInput.trim()) {
                        updateField(freePlan._id, 'features', [...freePlan.features, freePlanFeatureInput.trim()]);
                        setFreePlanFeatureInput('');
                      }
                    }}
                    sx={{ flex: 1, maxWidth: 420 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={!freePlanFeatureInput.trim()}
                    onClick={() => {
                      updateField(freePlan._id, 'features', [...freePlan.features, freePlanFeatureInput.trim()]);
                      setFreePlanFeatureInput('');
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={freePlanSaving || !freePlan.name.trim()}
                onClick={saveFreePlan}
              >
                {freePlanSaving ? 'Saving...' : 'Save Free Plan Settings'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      <Alert severity="info">Paid-plan entitlements control unlock quotas, export access, early access, top-ups, and team seats. Plans with active subscribers cannot be deleted.</Alert>
      {paidPlans.map((plan) => {
        const featureInput = getFeatureInput(plan._id);
        return (
          <Paper key={plan._id} variant="outlined" sx={{ p: 2, opacity: plan.isActive === false ? 0.6 : 1, borderColor: plan.isActive === false ? 'warning.main' : undefined }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TextField fullWidth label="Name" value={plan.name} onChange={(e) => updateField(plan._id, 'name', e.target.value)} />
                  {plan.isActive === false && <Chip label="Disabled" size="small" color="warning" />}
                </Stack>
              </Grid>
              <Grid item xs={6} md={1.5}><TextField fullWidth type="number" label="Price" value={plan.price} onChange={(e) => updateField(plan._id, 'price', Number(e.target.value))} /></Grid>
              <Grid item xs={6} md={1.5}><TextField fullWidth type="number" label="Monthly" value={plan.monthlyLeadLimit ?? ''} onChange={(e) => updateField(plan._id, 'monthlyLeadLimit', e.target.value === '' ? null : Number(e.target.value))} /></Grid>
              <Grid item xs={6} md={1.5}><TextField fullWidth type="number" label="Early min" value={plan.earlyAccessMinutes || 0} onChange={(e) => updateField(plan._id, 'earlyAccessMinutes', Number(e.target.value))} /></Grid>
              <Grid item xs={6} md={1.5}><TextField fullWidth type="number" label="Seats" value={plan.teamSeats || 1} onChange={(e) => updateField(plan._id, 'teamSeats', Number(e.target.value))} /></Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <FormControlLabel control={<Switch size="small" checked={plan.isActive !== false} onChange={(e) => updateField(plan._id, 'isActive', e.target.checked)} />} label="Active" />
                  <FormControlLabel control={<Switch size="small" checked={Boolean(plan.topUpAllowed)} onChange={(e) => updateField(plan._id, 'topUpAllowed', e.target.checked)} />} label="Top-up" />
                  <FormControlLabel control={<Switch size="small" checked={Boolean(plan.canExport)} onChange={(e) => updateField(plan._id, 'canExport', e.target.checked)} />} label="Export" />
                </Stack>
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth variant="contained" disabled={savingId === plan._id} onClick={() => save(plan)}>
                    {savingId === plan._id ? 'Saving...' : 'Save'}
                  </Button>
                  <IconButton color="error" size="small" disabled={deletingId === plan._id} onClick={() => setConfirmDeletePlan(plan)} title="Delete plan">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (shown under plan name)"
                  value={plan.description || ''}
                  onChange={(e) => updateField(plan._id, 'description', e.target.value)}
                  size="small"
                />
              </Grid>

              {/* Features */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Feature bullet points
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                  {(plan.features || []).map((f, i) => (
                    <Chip
                      key={i}
                      label={f}
                      size="small"
                      onDelete={() => updateField(plan._id, 'features', plan.features.filter((_, fi) => fi !== i))}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                  {(plan.features || []).length === 0 && (
                    <Typography variant="caption" color="text.disabled">No features added yet.</Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="e.g. 50 ride unlocks per month"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(plan._id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && featureInput.trim()) {
                        updateField(plan._id, 'features', [...(plan.features || []), featureInput.trim()]);
                        setFeatureInput(plan._id, '');
                      }
                    }}
                    sx={{ flex: 1, maxWidth: 360 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={!featureInput.trim()}
                    onClick={() => {
                      updateField(plan._id, 'features', [...(plan.features || []), featureInput.trim()]);
                      setFeatureInput(plan._id, '');
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDeletePlan} onClose={() => setConfirmDeletePlan(null)}>
        <DialogTitle>Delete plan?</DialogTitle>
        <DialogContent>
          Delete "{confirmDeletePlan?.name}"? This will fail if any drivers are currently subscribed to this plan.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeletePlan(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!deletingId}
            onClick={() => confirmDeletePlan && deletePlan(confirmDeletePlan)}
          >
            {deletingId ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create new plan */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>New Plan</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}><TextField fullWidth label="Plan name" value={newPlan.name} onChange={(e) => setNewPlan((p) => ({ ...p, name: e.target.value }))} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Price" value={newPlan.price} inputProps={{ min: 1 }} onChange={(e) => setNewPlan((p) => ({ ...p, price: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Monthly unlocks" value={newPlan.monthlyLeadLimit} onChange={(e) => setNewPlan((p) => ({ ...p, monthlyLeadLimit: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Early minutes" value={newPlan.earlyAccessMinutes} onChange={(e) => setNewPlan((p) => ({ ...p, earlyAccessMinutes: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={1.5}><FormControlLabel control={<Switch checked={newPlan.topUpAllowed} onChange={(e) => setNewPlan((p) => ({ ...p, topUpAllowed: e.target.checked }))} />} label="Top-up" /></Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Description (shown under plan name)"
              value={newPlan.description}
              onChange={(e) => setNewPlan((p) => ({ ...p, description: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Feature bullet points</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
              {newPlan.features.map((f, i) => (
                <Chip
                  key={i}
                  label={f}
                  size="small"
                  onDelete={() => setNewPlan((p) => ({ ...p, features: p.features.filter((_, fi) => fi !== i) }))}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                placeholder="e.g. 10 ride unlocks per month"
                value={newPlanFeatureInput}
                onChange={(e) => setNewPlanFeatureInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newPlanFeatureInput.trim()) {
                    setNewPlan((p) => ({ ...p, features: [...p.features, newPlanFeatureInput.trim()] }));
                    setNewPlanFeatureInput('');
                  }
                }}
                sx={{ flex: 1, maxWidth: 360 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!newPlanFeatureInput.trim()}
                onClick={() => {
                  setNewPlan((p) => ({ ...p, features: [...p.features, newPlanFeatureInput.trim()] }));
                  setNewPlanFeatureInput('');
                }}
              >
                Add
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={1.5}><Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={create}>Create</Button></Grid>
        </Grid>
      </Paper>
    </Stack>
  );
};

// ── TopUps Tab ───────────────────────────────────────────────────────────────

const TopUpsTab: React.FC = () => {
  const [packs, setPacks] = useState<TopUpPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TopUpPack | null>(null);
  const [newPack, setNewPack] = useState<TopUpPack>(emptyPack());

  const fetchPacks = async () => {
    try {
      const res = await api.get('/admin/top-up-packs');
      const raw = Array.isArray(res.data) ? res.data : (res.data.packs || []);
      setPacks(raw.map(normalizePack));
    } catch {
      toast.error('Failed to load top-up packs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPacks(); }, []);

  const updatePack = (id: string, field: keyof TopUpPack, value: any) =>
    setPacks((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));

  const savePack = async (pack: TopUpPack) => {
    try {
      setSavingId(pack.id);
      await api.patch(`/admin/top-up-packs/${pack.id}`, pack);
      toast.success('Pack saved');
    } catch {
      toast.error('Failed to save pack');
    } finally {
      setSavingId(null);
    }
  };

  const deletePack = async (pack: TopUpPack) => {
    try {
      setDeletingId(pack.id);
      await api.delete(`/admin/top-up-packs/${pack.id}`);
      setPacks((prev) => prev.filter((p) => p.id !== pack.id));
      setConfirmDelete(null);
      toast.success('Pack deleted');
    } catch {
      toast.error('Failed to delete pack');
    } finally {
      setDeletingId(null);
    }
  };

  const createPack = async () => {
    if (!newPack.label.trim()) { toast.error('Pack label is required'); return; }
    try {
      const res = await api.post('/admin/top-up-packs', newPack);
      const raw = res.data?.pack || res.data;
      setPacks((prev) => [...prev, normalizePack(raw, prev.length)].sort((a, b) => a.sortOrder - b.sortOrder));
      setNewPack(emptyPack((packs.length + 1) * 10));
      toast.success('Pack created');
    } catch {
      toast.error('Failed to create pack');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Stack spacing={2}>
      {packs.length === 0 && <Alert severity="info">No top-up packs yet. Create one below.</Alert>}
      {packs.map((pack) => (
        <Paper key={pack.id} variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2.5}>
              <TextField fullWidth label="Label" value={pack.label} onChange={(e) => updatePack(pack.id, 'label', e.target.value)} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth type="number" label="Credits" value={pack.credits} onChange={(e) => updatePack(pack.id, 'credits', Number(e.target.value))} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth type="number" label="Price" value={pack.price} onChange={(e) => updatePack(pack.id, 'price', Number(e.target.value))} />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField fullWidth type="number" label="Order" value={pack.sortOrder} onChange={(e) => updatePack(pack.id, 'sortOrder', Number(e.target.value))} />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Stack spacing={0}>
                <FormControlLabel control={<Switch size="small" checked={pack.isActive} onChange={(e) => updatePack(pack.id, 'isActive', e.target.checked)} />} label="Active" />
                <FormControlLabel control={<Switch size="small" checked={pack.popular} onChange={(e) => updatePack(pack.id, 'popular', e.target.checked)} />} label="Popular" />
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  disabled={savingId === pack.id}
                  onClick={() => savePack(pack)}
                  sx={{ flex: 1 }}
                >
                  {savingId === pack.id ? 'Saving…' : 'Save'}
                </Button>
                <IconButton color="error" size="small" onClick={() => setConfirmDelete(pack)} disabled={deletingId === pack.id}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              {pack.popular && <Chip label="Popular" color="primary" size="small" sx={{ mt: 0.5 }} />}
              {!pack.isActive && <Chip label="Inactive" size="small" sx={{ mt: 0.5 }} />}
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Create new pack */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>New Top-up Pack</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}><TextField fullWidth label="Label" value={newPack.label} onChange={(e) => setNewPack((p) => ({ ...p, label: e.target.value }))} /></Grid>
          <Grid item xs={6} md={1.5}><TextField fullWidth type="number" label="Credits" value={newPack.credits} onChange={(e) => setNewPack((p) => ({ ...p, credits: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={1.5}><TextField fullWidth type="number" label="Price (₹)" value={newPack.price} onChange={(e) => setNewPack((p) => ({ ...p, price: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={1}><TextField fullWidth type="number" label="Order" value={newPack.sortOrder} onChange={(e) => setNewPack((p) => ({ ...p, sortOrder: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={2}>
            <Stack>
              <FormControlLabel control={<Switch size="small" checked={newPack.isActive} onChange={(e) => setNewPack((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
              <FormControlLabel control={<Switch size="small" checked={newPack.popular} onChange={(e) => setNewPack((p) => ({ ...p, popular: e.target.checked }))} />} label="Popular" />
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}><Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={createPack}>Create Pack</Button></Grid>
        </Grid>
      </Paper>

      {/* Delete confirm dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete pack?</DialogTitle>
        <DialogContent>Delete "{confirmDelete?.label}"? This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!!deletingId} onClick={() => confirmDelete && deletePack(confirmDelete)}>
            {deletingId ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

// ── Coupons Tab ──────────────────────────────────────────────────────────────

const CouponsTab: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCoupon, setNewCoupon] = useState({
    code: '', discountType: 'percent' as 'percent' | 'fixed', discountValue: 10, maxRedemptions: 100,
  });

  useEffect(() => {
    adminService.getCoupons().then(setCoupons).catch(() => toast.error('Failed to load coupons')).finally(() => setLoading(false));
  }, []);

  const create = async () => {
    if (!newCoupon.code.trim()) { toast.error('Coupon code is required'); return; }
    try {
      const created = await adminService.createCoupon({
        code: newCoupon.code.trim().toUpperCase(),
        discountType: newCoupon.discountType,
        discountValue: Number(newCoupon.discountValue),
        maxRedemptions: Number(newCoupon.maxRedemptions) || null,
        isActive: true,
      });
      setCoupons((prev) => [created, ...prev]);
      setNewCoupon({ code: '', discountType: 'percent', discountValue: 10, maxRedemptions: 100 });
      toast.success('Coupon created');
    } catch {
      toast.error('Failed to create coupon');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Create discount codes for plan purchases and top-up packs.</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}><TextField fullWidth label="Code" value={newCoupon.code} onChange={(e) => setNewCoupon((p) => ({ ...p, code: e.target.value.toUpperCase() }))} /></Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={newCoupon.discountType} label="Type" onChange={(e) => setNewCoupon((p) => ({ ...p, discountType: e.target.value as any }))}>
                <MenuItem value="percent">Percent</MenuItem>
                <MenuItem value="fixed">Fixed INR</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Discount" value={newCoupon.discountValue} onChange={(e) => setNewCoupon((p) => ({ ...p, discountValue: Number(e.target.value) }))} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Max uses" value={newCoupon.maxRedemptions} onChange={(e) => setNewCoupon((p) => ({ ...p, maxRedemptions: Number(e.target.value) }))} /></Grid>
          <Grid item xs={12} md={3}><Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={create}>Create coupon</Button></Grid>
        </Grid>
      </Paper>

      <Box display="flex" flexWrap="wrap" gap={1}>
        {coupons.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No coupons yet.</Typography>
        ) : coupons.map((c) => (
          <Chip
            key={c._id}
            label={`${c.code}: ${c.discountType === 'percent' ? `${c.discountValue}%` : `₹${c.discountValue}`} (${c.redeemedCount}${c.maxRedemptions ? `/${c.maxRedemptions}` : ''})`}
            color={c.isActive ? 'primary' : 'default'}
            variant="outlined"
          />
        ))}
      </Box>
    </Stack>
  );
};

// ── Feature Comparison Tab ───────────────────────────────────────────────────

const FeatureComparisonTab: React.FC = () => {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [features, setFeatures] = useState<ComparisonFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [newRowName, setNewRowName] = useState('');
  const [newRowPlanIds, setNewRowPlanIds] = useState<string[]>([]);
  const [addingRow, setAddingRow] = useState(false);

  useEffect(() => {
    Promise.all([adminService.getPlans(), adminService.getComparisonFeatures()])
      .then(([loadedPlans, loadedFeatures]) => {
        setPlans(loadedPlans);
        setFeatures(loadedFeatures);
      })
      .catch(() => toast.error('Failed to load comparison data'))
      .finally(() => setLoading(false));
  }, []);

  const togglePlan = async (feature: ComparisonFeature, planId: string, checked: boolean) => {
    const nextPlanIds = checked
      ? [...feature.planIds, planId]
      : feature.planIds.filter((id) => id !== planId);
    const prev = feature;
    setFeatures((fs) => fs.map((f) => f._id === feature._id ? { ...f, planIds: nextPlanIds } : f));
    setSavingId(feature._id);
    try {
      await adminService.updateComparisonFeature(feature._id, { planIds: nextPlanIds });
    } catch {
      toast.error('Failed to save');
      setFeatures((fs) => fs.map((f) => f._id === prev._id ? prev : f));
    } finally {
      setSavingId(null);
    }
  };

  const addRow = async () => {
    if (!newRowName.trim()) { toast.error('Feature name is required'); return; }
    setAddingRow(true);
    try {
      const created = await adminService.createComparisonFeature({
        name: newRowName.trim(),
        planIds: newRowPlanIds,
        sortOrder: (features.length + 1) * 10,
      });
      setFeatures((fs) => [...fs, created]);
      setNewRowName('');
      setNewRowPlanIds([]);
      toast.success('Feature row added');
    } catch {
      toast.error('Failed to add feature');
    } finally {
      setAddingRow(false);
    }
  };

  const deleteFeature = async (id: string) => {
    setDeletingId(id);
    try {
      await adminService.deleteComparisonFeature(id);
      setFeatures((fs) => fs.filter((f) => f._id !== id));
      toast.success('Feature deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Each row is a feature shown in the Full feature comparison table on the /plans page.
        Check the plans that include that feature. Changes save instantly.
      </Alert>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Feature</TableCell>
                {plans.map((plan) => (
                  <TableCell key={plan._id} align="center" sx={{ fontWeight: 700, minWidth: 90 }}>
                    {plan.name}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {features.length === 0 && (
                <TableRow>
                  <TableCell colSpan={plans.length + 2}>
                    <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                      No features yet. Add one below.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {features.map((feature) => (
                <TableRow key={feature._id} hover>
                  <TableCell>{feature.name}</TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan._id} align="center" padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={feature.planIds.includes(plan._id)}
                        disabled={savingId === feature._id}
                        onChange={(e) => togglePlan(feature, plan._id, e.target.checked)}
                      />
                    </TableCell>
                  ))}
                  <TableCell align="center" padding="none">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deletingId === feature._id}
                      onClick={() => deleteFeature(feature._id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {/* Inline new-row */}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="New feature name"
                    value={newRowName}
                    onChange={(e) => setNewRowName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addRow()}
                    fullWidth
                  />
                </TableCell>
                {plans.map((plan) => (
                  <TableCell key={plan._id} align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={newRowPlanIds.includes(plan._id)}
                      onChange={(e) =>
                        setNewRowPlanIds((prev) =>
                          e.target.checked ? [...prev, plan._id] : prev.filter((id) => id !== plan._id)
                        )
                      }
                    />
                  </TableCell>
                ))}
                <TableCell align="center" padding="none">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={addingRow || !newRowName.trim()}
                    onClick={addRow}
                    sx={{ whiteSpace: 'nowrap', mr: 0.5 }}
                  >
                    Add
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};

export default OperationsPlans;
